import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Users, CalendarClock, CalendarX2, Send, Phone, Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import AgendaCalendar from "../components/AgendaCalendar";

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div data-testid={testid} className="p-5 sm:p-6 rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow animate-fade-up">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <div className="mt-3 font-head text-3xl font-bold text-slate-900">{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [contatti, setContatti] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const openContatti = async () => {
    const { data } = await api.get("/clienti-da-contattare");
    setContatti(data);
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Panoramica clienti, richiami e comunicazioni.</p>
        </div>
        <Button data-testid="recall-clients-btn" onClick={openContatti} className="bg-[#1E40AF] hover:bg-[#1D4ED8] h-11">
          <CalendarClock className="h-4 w-4 mr-2" /> Clienti da contattare
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="stat-totale-clienti" icon={Users} label="Totale clienti" value={data?.totale_clienti ?? "—"} tone="bg-[#1E40AF]/10 text-[#1E40AF]" />
        <StatCard testid="stat-richiamo-24" icon={CalendarClock} label="Da richiamare · 24 mesi" value={data?.richiamo_24 ?? "—"} tone="bg-amber-100 text-amber-700" />
        <StatCard testid="stat-richiamo-36" icon={CalendarX2} label="Da richiamare · 36 mesi" value={data?.richiamo_36 ?? "—"} tone="bg-red-100 text-red-700" />
        <StatCard testid="stat-campagne" icon={Send} label="Campagne inviate" value={data?.campagne_inviate ?? "—"} tone="bg-emerald-100 text-emerald-700" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-head text-lg font-semibold text-slate-900">Ultime comunicazioni</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {(data?.ultime_comunicazioni || []).length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-slate-500">Nessuna campagna inviata.</div>
          )}
          {(data?.ultime_comunicazioni || []).map((c) => (
            <div key={c.id} className="px-6 py-3.5 flex items-center gap-4">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${c.canale === "whatsapp" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                {c.canale === "whatsapp" ? <Phone className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{c.nome}</div>
                <div className="text-xs text-slate-500">{c.tipo === "promozione" ? "Promozione" : "Richiamo controllo"} · {c.data}</div>
              </div>
              <Badge variant="secondary" className="shrink-0">{c.destinatari} destinatari</Badge>
            </div>
          ))}
        </div>
      </div>

      <AgendaCalendar />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="recall-modal">
          <DialogHeader>
            <DialogTitle className="font-head">Clienti da contattare ({contatti.length})</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto custom-scroll -mx-2 px-2">
            {contatti.length === 0 && <div className="py-8 text-center text-sm text-slate-500">Nessun cliente da richiamare.</div>}
            {contatti.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{c.cognome} {c.nome}</div>
                  <div className="text-xs text-slate-500">{c.telefono || "—"} · {c.email || "—"}</div>
                </div>
                {c.segmenti?.includes("richiamo_36") ? (
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">36+ mesi</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">24+ mesi</Badge>
                )}
                <Button size="sm" variant="ghost" onClick={() => { setOpen(false); navigate(`/clienti/${c.id}`); }} data-testid={`open-cliente-${c.id}`}>
                  Apri <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
